import { marquesMontrees } from '@/lib/ajouts-texte';
import {
  INDICATEURS_AFFICHES,
  type LectureControle,
  STATUT_INFORMATION,
  indicateur,
  passagesBloquants,
  phraseLongueur,
} from '@/lib/controle';
import type { ControleNoyau, IndicateurControle } from '@rosetta/shared';
import { type ReactNode, useId } from 'react';
import { CarteAjouts, CarteChangements, indicateurAjoutsAffiche } from './ajouts-changements';
import { CarteIndicateur } from './carte-indicateur';
import { ChargementPanneau } from './chargement-panneau';
import { ColonneTexte } from './colonne-texte';
import { DetailAffirmations } from './detail-affirmations';
import { DetailMiseEnForme } from './detail-mise-en-forme';
import { EchelleLongueur } from './echelle-longueur';
import { LectureControleImpossible } from './lecture-controle-impossible';
import { LegendeMarques } from './legende-marques';
import { MessageIndisponible } from './message-indisponible';
import { SyntheseControle } from './synthese-controle';

interface VueTexteTraduitProps {
  /** The translated text of the noyau. */
  texte: string;
  controle: LectureControle;
  /** "réessayer" of a control that could not be read (#34): reads it again. */
  onReessayer?: (() => void) | undefined;
  /** A new read is under way: the button waits. */
  reessaiEnCours?: boolean | undefined;
  /** Goes to the "Avant / après" tab, where the additions are marked (#23). */
  onVoirAvantApres?: (() => void) | undefined;
}

interface Carte {
  numero: string;
  contenu: ReactNode;
}

/** The cards of a theme ("Contenu", "Forme"), under their title; nothing without a card. */
function Theme({ titre, cartes }: { titre: string; cartes: readonly Carte[] }) {
  const idTitre = useId();
  if (cartes.length === 0) return null;
  return (
    <section aria-labelledby={idTitre} className="[&+&]:mt-6">
      <h3 id={idTitre} className="font-bold text-body text-gray-700 leading-[1.4]">
        {titre}
      </h3>
      {cartes.map((carte) => (
        <div key={carte.numero}>{carte.contenu}</div>
      ))}
    </section>
  );
}

/** The cards of the indicators Tahiti gave, by theme, in the order of A8. */
function cartesDe(controle: ControleNoyau): {
  analyse: Carte[];
  contenu: Carte[];
  forme: Carte[];
} {
  const carte = (
    numero: string,
    detail: (indicateur: IndicateurControle) => ReactNode,
    avecResume = true,
    resume?: (indicateur: IndicateurControle) => string | undefined,
  ): Carte[] => {
    const trouve = indicateur(controle, numero);
    if (trouve === undefined) return [];
    return [
      {
        numero,
        contenu: (
          <CarteIndicateur indicateur={trouve} avecResume={avecResume} resume={resume?.(trouve)}>
            {detail(trouve)}
          </CarteIndicateur>
        ),
      },
    ];
  };
  const { affirmations, longueur, miseEnForme } = controle;
  const ajoutsAffiches = indicateurAjoutsAffiche(controle);
  const changements = controle.changements ?? [];
  return {
    // The indicators Tahiti marks "Information" (#30, the tone 1.5): their card says what Tahiti
    // says of them, the reasons of their points as they are, and is where their counter leads.
    analyse: controle.indicateurs
      .filter((candidat) => candidat.statut.code === STATUT_INFORMATION)
      .map((trouve) => ({
        numero: trouve.numero,
        contenu: (
          <CarteIndicateur indicateur={trouve}>
            {trouve.points.length === 0 ? null : (
              <ul className="mt-2 flex flex-col gap-2 text-gray-700 text-small leading-normal">
                {trouve.points.map((point) => (
                  <li key={point.raison}>{point.raison}</li>
                ))}
              </ul>
            )}
          </CarteIndicateur>
        ),
      })),
    contenu: [
      ...carte(INDICATEURS_AFFICHES.affirmations, () =>
        affirmations === null ? null : <DetailAffirmations affirmations={affirmations} />,
      ),
      // The additions (2.3) follow the affirmations as in the numbering of Tahiti, and the changes
      // the translation declares go with them (#30): the cards are in the column of the themes.
      ...(ajoutsAffiches === undefined
        ? []
        : [
            {
              numero: ajoutsAffiches.numero,
              contenu: <CarteAjouts controle={controle} indicateur={ajoutsAffiches} />,
            },
          ]),
      ...(changements.length === 0
        ? []
        : [{ numero: 'changements', contenu: <CarteChangements changements={changements} /> }]),
    ],
    forme: [
      ...carte(
        INDICATEURS_AFFICHES.longueur,
        ({ statut }) =>
          longueur === null ? null : <EchelleLongueur longueur={longueur} statut={statut.code} />,
        true,
        // Our sentence from the numbers; without them, the summary of Tahiti stays.
        ({ statut }) => (longueur === null ? undefined : phraseLongueur(longueur, statut.code)),
      ),
      ...carte(
        INDICATEURS_AFFICHES.miseEnForme,
        () => (miseEnForme === null ? null : <DetailMiseEnForme miseEnForme={miseEnForme} />),
        // The summary of Tahiti for 3.2 is "1 paragraphes pour 1": the card words the numbers.
        miseEnForme === null,
      ),
    ],
  };
}

/**
 * Panel of the "Texte traduit" tab (A8, #10): what the quality control of Tahiti says about the
 * translated text of the noyau shown. On top, the summary (points to look at, counters); then the
 * key of the marks (#35, the same `LegendeMarques` as "Avant / après", only when a mark is shown);
 * then the text on the left, marked exactly as the translated text of "Avant / après" is (#35: the
 * same `ColonneTexte`, so the additions and the changes the translation declares have the same
 * tints, names and bubbles, the addition winning over a change), plus the passages of the
 * "Bloquant" points of the other indicators, in a neutral tint (red is the colour of the additions),
 * and the cards by theme on the right: "Analyse" (the indicators Tahiti marks "Information"),
 * "Contenu" (faithfulness of the affirmations, then the additions of the control and the changes
 * the translation declares: #30, listed, the additions being the target of the counters) and
 * "Forme" (length, layout, rewrite). Contresens are not there: Tahiti does not give them. No
 * control (none, a failed one): the text as it is, and a sober message where the control would be
 * (A10); a read that failed (#34) says so and offers "réessayer".
 */
export function VueTexteTraduit({
  texte,
  controle,
  onReessayer,
  reessaiEnCours,
  onVoirAvantApres,
}: VueTexteTraduitProps) {
  const idTitre = useId();
  const panneau = (contenu: ReactNode) => <div>{contenu}</div>;

  if (typeof controle === 'string') {
    return panneau(
      <div className="grid grid-cols-1 items-start gap-12 xl:grid-cols-[minmax(0,1fr)_600px]">
        <ColonneTexte titre="Texte traduit" texte={texte} />
        <section aria-labelledby={idTitre} className="min-w-0">
          <h2 id={idTitre} className="font-semibold text-body-lg leading-[1.3]">
            Contrôle qualité
          </h2>
          {controle === 'chargement' ? (
            <ChargementPanneau>Chargement du contrôle qualité...</ChargementPanneau>
          ) : controle === 'illisible' ? (
            <LectureControleImpossible onReessayer={onReessayer} enCours={reessaiEnCours} />
          ) : (
            <MessageIndisponible>
              Le contrôle qualité de cette traduction n'est pas disponible.
            </MessageIndisponible>
          )}
        </section>
      </div>,
    );
  }

  const { analyse, contenu, forme } = cartesDe(controle);
  // The additions have their card in "Contenu" (#30): the target of their point in the counters.
  const avecCarte = new Set([...analyse, ...contenu, ...forme].map((carte) => carte.numero));
  // The text is marked as in "Avant / après" (#35): the additions, the changes the translation
  // declares, and the passages of the other "Bloquant" points (the additions are not one of them).
  const ajouts = controle.ajouts ?? [];
  const changements = controle.changements ?? [];
  const passages = passagesBloquants(controle, [INDICATEURS_AFFICHES.ajouts]);
  const montrees = marquesMontrees(texte, ajouts, changements, passages);
  const avecLegende = montrees.ajouts || montrees.changements || montrees.passages;
  return panneau(
    <>
      <SyntheseControle
        controle={controle}
        avecCarte={avecCarte}
        onVoirAvantApres={onVoirAvantApres}
      />
      {avecLegende ? (
        <div className="mt-6">
          <LegendeMarques
            avecInvariants={false}
            avecAjouts={montrees.ajouts}
            avecChangements={montrees.changements}
            avecPassages={montrees.passages}
          />
        </div>
      ) : null}
      <div className="mt-6 grid grid-cols-1 items-start gap-12 xl:grid-cols-[minmax(0,1fr)_600px]">
        <ColonneTexte
          titre="Texte traduit"
          texte={texte}
          ajouts={ajouts}
          changements={changements}
          passages={passages}
        />
        <div className="min-w-0">
          <Theme titre="Analyse" cartes={analyse} />
          <Theme titre="Contenu" cartes={contenu} />
          <Theme titre="Forme" cartes={forme} />
        </div>
      </div>
    </>,
  );
}
